/**
 * HomeCtaBanner · v2.7.65+E261
 * ============================
 * Public home-page call-to-action banner (drink-driving / ride-home urgency).
 * - Admin toggle + editable text via GET /api/site/home-banner (CMS).
 * - Contained to the SAME max-w-7xl width as the hero; YELLOW card with RED
 *   accents; fully rounded (incl. bottom edges).
 * - Plays a one-time entrance + shine on load.
 * - Visitor can HIDE it (X) — it slides UP and collapses (state remembered in
 *   localStorage). A slim pill lets them toggle it back ON.
 * Renders nothing when the admin has it disabled.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, AlertTriangle, X } from "lucide-react";
import api from "@/lib/api";

const HIDE_KEY = "bt_cta_banner_hidden";

function BookCta({ link, label }) {
  const cls = "flex-1 inline-flex items-center justify-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white font-extrabold uppercase tracking-wider px-6 py-3 rounded-md transition-colors";
  if (link && /^https?:\/\//i.test(link)) {
    return <a href={link} className={cls} data-testid="cta-banner-book">{label} <ArrowRight className="h-5 w-5"/></a>;
  }
  return <Link to={link || "/ride/login"} className={cls} data-testid="cta-banner-book">{label} <ArrowRight className="h-5 w-5"/></Link>;
}

export default function HomeCtaBanner() {
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(HIDE_KEY) === "1"; } catch { return false; }
  });
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    api.get("/site/home-banner").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, []);

  if (!cfg || !cfg.enabled) return null;

  const tel = `tel:${(cfg.phone || "").replace(/\D/g, "")}`;

  const hide = () => {
    setClosing(true);
    setTimeout(() => {
      try { localStorage.setItem(HIDE_KEY, "1"); } catch { /* ignore */ }
      setDismissed(true);
      setClosing(false);
    }, 400);
  };

  const reopen = () => {
    try { localStorage.removeItem(HIDE_KEY); } catch { /* ignore */ }
    setDismissed(false);
  };

  // Collapsed → slim toggle pill so riders can bring it back.
  if (dismissed) {
    return (
      <div className="max-w-7xl mx-auto px-5 pt-3" data-testid="cta-banner-wrap">
        <button
          type="button"
          onClick={reopen}
          data-testid="cta-banner-reopen"
          className="cta-reopen inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-zinc-950 font-black text-[11px] uppercase tracking-wider px-4 py-2 shadow-md ring-1 ring-amber-600/40 transition-colors"
        >
          <AlertTriangle className="h-4 w-4 text-red-700"/> Ride-home safety notice — Show
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 pt-3" data-testid="cta-banner-wrap">
      <div
        className={`relative overflow-hidden rounded-2xl bg-amber-400 shadow-xl shadow-amber-500/25 ring-1 ring-amber-600/40 cta-shine ${closing ? "cta-banner-out" : "cta-banner-in"}`}
        data-testid="cta-banner"
      >
        {/* faint diagonal texture */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "repeating-linear-gradient(45deg, #000 0 2px, transparent 2px 16px)" }} aria-hidden="true"/>

        {/* hide button */}
        <button
          type="button"
          onClick={hide}
          data-testid="cta-banner-hide"
          aria-label="Hide banner"
          className="absolute top-2.5 right-2.5 z-20 h-8 w-8 inline-flex items-center justify-center rounded-full bg-zinc-950/10 hover:bg-zinc-950/25 text-zinc-900 transition-colors"
        >
          <X className="h-4.5 w-4.5"/>
        </button>

        <div className="relative px-5 sm:px-7 py-6 sm:py-7 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-8">
          <div className="flex-1 min-w-0 pr-8">
            {cfg.eyebrow ? (
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] font-black text-red-700 mb-2">
                <AlertTriangle className="h-4 w-4"/> {cfg.eyebrow}
              </div>
            ) : null}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black leading-tight text-zinc-950" data-testid="cta-banner-headline">
              {cfg.headline_1 ? <>{cfg.headline_1}{" "}</> : null}
              {cfg.headline_highlight ? <span className="text-red-700">{cfg.headline_highlight}{" "}</span> : null}
              {cfg.headline_2 ? <span className="underline decoration-red-700/70 decoration-4 underline-offset-4">{cfg.headline_2}</span> : null}
            </h2>
            {cfg.subheadline ? (
              <p className="text-sm sm:text-base font-bold text-zinc-900 mt-2" data-testid="cta-banner-subhead">{cfg.subheadline}</p>
            ) : null}
            {cfg.support_line ? (
              <p className="text-xs sm:text-sm text-zinc-800 mt-1.5 max-w-3xl" data-testid="cta-banner-support">{cfg.support_line}</p>
            ) : null}
          </div>

          <div className="flex flex-col items-stretch gap-3 shrink-0 w-full lg:w-auto">
            {cfg.phone ? (
              <a href={tel} className="w-full inline-flex items-center justify-center gap-2.5 rounded-md bg-white text-red-700 font-black text-3xl sm:text-4xl leading-none tracking-tight px-4 py-3 shadow-md ring-1 ring-red-600/20 hover:bg-red-50 transition-colors" data-testid="cta-banner-phone">
                <Phone className="h-7 w-7 sm:h-8 sm:w-8"/> {cfg.phone}
              </a>
            ) : null}
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <a href={tel} data-testid="cta-banner-call" className="cta-call-pulse flex-1 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-extrabold uppercase tracking-wider px-6 py-3 rounded-md shadow-lg transition-colors">
                <Phone className="h-5 w-5"/> {cfg.call_label || "Call Buller Taxi"}
              </a>
              <BookCta link={cfg.book_link} label={cfg.book_label || "Book Online"}/>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
